import { useAxios } from '../../Helpers/AxiosHelper';
import { useEffect, useState } from 'react';
import {  useForm } from 'react-hook-form';
import { useToast } from '../../Helpers/ToastHelper';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { getSettings } from '../../Helpers/SettingsHelper';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { Controller, Dialog, FloatLabel, InputText, ProgressBar, ProgressSpinner, SCButton, SubmitHandler, EditorTextChangeEvent, DDBrands, DDCountries, Editor, DDMailTemplateType } from "../../Common/Imports"
import { ApiResponse, ConfigMailTemplate_EXT } from '../../Models/Classes';
import { DecodeHtml } from '../../Common/Utility/DecodeHtml';
import { useEditorServerSync } from '../../Common/Hooks/useEditorServerSync';

interface ConfigMailTemplatesEditProps {
    IdTemplate: number | undefined;
    OnSave?: () => void;
}

const ConfigMailTemplatesEdit: React.FC<ConfigMailTemplatesEditProps> = ({ IdTemplate, OnSave }) => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [Data, setData] = useState<ConfigMailTemplate_EXT | undefined>();
    const {
        handleSubmit,
        formState: { errors },
        reset,
        control,
        setValue // Added setValue for controlling the form value
    } = useForm<ConfigMailTemplate_EXT>();
    const config = getSettings();
    const AxiosHelper = useAxios();
    const { editorRef, onLoad: onEditorLoad, reset: resetEditorSync, markApplied: markEditorUserEdited } = useEditorServerSync(Data?.value ?? '');

    useEffect(() => {
        const fetchBrand = async () => {
            try {
                const response = await AxiosHelper.get<ApiResponse<ConfigMailTemplate_EXT>>(`StakeholderConfigs/MailTemplates/GetConfig/` + IdTemplate);
                resetEditorSync();
                setData(response.data.data);
                reset(response.data.data);
            } catch (error) {
                console.error('Error fetching user data:', error);
                showToast('error', 'Error', t("Common.Toasts.Fetch.Error"));
            }
        };
        fetchBrand();
    }, [reset, AxiosHelper, config, t, showToast, IdTemplate, resetEditorSync]);

    const onSubmit: SubmitHandler<ConfigMailTemplate_EXT> = async (data: ConfigMailTemplate_EXT) => {
        data.value = DOMPurify.sanitize(data.value);
        try {
            await AxiosHelper.post<ApiResponse<ConfigMailTemplate_EXT>>(`StakeholderConfigs/MailTemplates/SaveConfig`, data);
            showToast('success', 'Success', t("Common.Toasts.Save.Success"));
            if (OnSave) {
                OnSave();
            }
        } catch (error: unknown) {
            console.log(error);
            showToast('error', 'Error', t("Common.Toasts.Save.Error"));
        }
    };

    if (!Data) {
        return <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
    }

    const handleEditorChange = (e: EditorTextChangeEvent) => {
        // Quill re-emits text-change while loading the server HTML, with its own normalized
        // markup (tables, divs and inline styles stripped). Only real edits happen with focus:
        // ignore the rest so preview and saved value keep the original template HTML.
        if (!editorRef.current?.getQuill()?.hasFocus()) {
            return;
        }
        if (e.source === 'user') {
            markEditorUserEdited();
        }
        const newValue = e.htmlValue ?? ""; // Ensure that newValue is never null, default to empty string
        setValue('value', newValue); // Update form value
        setData((prevData) =>
            prevData ? { ...prevData, value: newValue } : undefined // Ensure value is updated
        );
    };
    const footerTemplate = () => (
        <div className="col d-flex justify-content-end">
            <SCButton Name="Save" Label={t("Common.Buttons.Save")} OnClick={handleSubmit(onSubmit)} />
        </div>
    );
    return (
        <Dialog
            maximized
            header="Edit Mail Template"
            visible={Data ? true : false}
            onHide={() => {
                setData(undefined); if (OnSave) {
                    OnSave();
                }
            }}
            footer={footerTemplate}
        >
            {Data ?
                <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
                    <div className="row mb-3 mt-3">
                        <div className="col-md-6 mb-4">
                            <div className="row">
                                <Controller
                                    name="idCountry"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <div className="col-md-4 mb-4">
                                            <FloatLabel>
                                                <DDCountries
                                                    value={field.value}
                                                    onChange={(value) => field.onChange(value)}
                                                    onBlur={field.onBlur}
                                                />
                                                <label htmlFor="country">{t("Stakeholder.Configs_MailTemplate.Edit.Country")}</label>
                                            </FloatLabel>
                                            {fieldState.invalid && (
                                                <small className="p-error">
                                                    {fieldState.error?.message}
                                                </small>
                                            )}
                                        </div>
                                    )}
                                />
                                <Controller
                                    name="idBrand"
                                    control={control}
                                    render={({ field, fieldState }) => (
                                        <div className="col-md-4 mb-4">
                                            <FloatLabel>
                                                <DDBrands
                                                    IdStakeholder={Data.idStakeholder}
                                                    value={field.value}
                                                    onChange={(value) => field.onChange(value)}
                                                    onBlur={field.onBlur}
                                                />
                                                <label htmlFor="country">{t("Stakeholder.Configs_MailTemplate.Edit.Brand")}</label>
                                            </FloatLabel>
                                            {fieldState.invalid && (
                                                <small className="p-error">
                                                    {fieldState.error?.message}
                                                </small>
                                            )}
                                        </div>
                                    )}
                                />
                                <Controller
                                    name="type"
                                    control={control}
                                    rules={{ required: `${t("Stakeholder.Configs_MailTemplate.Edit.Type")} ${t("Common.Form.Required")}` }}
                                    render={({ field, fieldState }) => (
                                        <div className="col-md-4 mb-4">
                                            <FloatLabel>
                                                <DDMailTemplateType
                                                    IdStakeholder={Data.idStakeholder}
                                                    value={field.value}
                                                    onChange={(value) => field.onChange(value)}
                                                    onBlur={field.onBlur}
                                                />
                                                <label htmlFor="country">{t("Stakeholder.Configs_MailTemplate.Edit.Type")}</label>
                                            </FloatLabel>
                                            {fieldState.invalid && (
                                                <small className="p-error">
                                                    {fieldState.error?.message}
                                                </small>
                                            )}
                                        </div>
                                    )}
                                />
                            </div>
                            <div className="row">
                                <div className="col-md-12 mb-2">
                                    <Controller
                                        name="provider"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <div className="col-md-12 mb-4">
                                                <FloatLabel>
                                                    <InputText
                                                        value={field.value ?? ""}
                                                        onChange={(value) => field.onChange(value)}
                                                        onBlur={field.onBlur}
                                                    />
                                                    <label htmlFor="provider">{t("Stakeholder.Configs_MailTemplate.Edit.Provider")}</label>
                                                </FloatLabel>
                                                {fieldState.invalid && (
                                                    <small className="p-error">
                                                        {fieldState.error?.message}
                                                    </small>
                                                )}
                                            </div>
                                        )}
                                    />
                                    <Controller
                                        name="subject"
                                        control={control}
                                        rules={{ required: `${t("Stakeholder.Configs_MailTemplate.Edit.Subject")} ${t("Common.Form.Required")}` }}
                                        render={({ field, fieldState }) => (
                                            <div className="col-md-12 mb-4">
                                                <FloatLabel>
                                                    <InputText
                                                        value={field.value}
                                                        onChange={(value) => field.onChange(value)}
                                                        onBlur={field.onBlur}
                                                    />
                                                    <label htmlFor="subject">{t("Stakeholder.Configs_MailTemplate.Edit.Subject")}</label>
                                                </FloatLabel>
                                                {fieldState.invalid && (
                                                    <small className="p-error">
                                                        {fieldState.error?.message}
                                                    </small>
                                                )}
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-12 mb-2">
                                    <Controller
                                        name="value"
                                        control={control}
                                        rules={{ required: t("Stakeholder.Configs_MailTemplate.Edit.value") + " " + t("Auth.Register.Form.Required") }}
                                        render={({ field }) => (
                                            <Editor
                                                id="value"
                                                {...field}
                                                ref={editorRef}
                                                value={Data?.value ?? ""} // Bind Editor value to the Data state
                                                onLoad={onEditorLoad}
                                                onTextChange={handleEditorChange} // Update Data and form value on change
                                                className={classNames({ 'p-invalid': errors.value })}
                                            />
                                        )}
                                    />
                                    {errors.value && (
                                        <small className="p-error">
                                            {errors.value.message}
                                        </small>
                                    )}
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-12">
                                    NB: You can use the following placeholders to compose you email:
                                    <ul>
                                        <li>
                                            [BrandName]
                                        </li>
                                        <li>
                                            [RefPaxName]
                                        </li>
                                        <li>
                                            [StructureName]
                                        </li>
                                        <li>
                                            [CheckInDate]
                                        </li>
                                        <li>
                                            [ProviderName]
                                        </li>
                                        <li>
                                            [ProviderCode]
                                        </li>
                                        <li>
                                            [BookingNumber]
                                        </li>
                                        <li>
                                            [CheckOutDate]
                                        </li>
                                        <li>
                                            [RoomName]
                                        </li>
                                        <li>
                                            [MealplanName]
                                        </li>
                                        <li>
                                            [PaxList]
                                        </li>
                                        <li>
                                            [CINMessage]
                                        </li>
                                        <li>
                                            [Error] - Only on Provider Mail Template
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mb-4">
                            {parse(DecodeHtml(Data?.value))}
                        </div>
                    </div>
                </form> :
                <ProgressSpinner />
            }
        </Dialog >
    );
};

export default ConfigMailTemplatesEdit;
